import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Chip,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Stack,
  Paper,
  FormControlLabel,
  Switch,
  Tooltip,
  Dialog
} from '@mui/material';
import {
  Add,
  Delete,
  Save,
  Close,
  Schedule,
  Assignment,
  Class,
  Subject,
  AccessTime,
  CheckCircle,
  Info,
  ContentCopy,
} from '@mui/icons-material';

interface Question {
  id: string;
  question: string;
  type: 'MCQ' | 'Short' | 'Long';
  marks: number;
  options?: string[];
  correctAnswer?: string;
}

interface CreateTestProps {
  userType: 'parent' | 'teacher';
  onClose?: () => void;
  onSubmit?: (testData: any) => void;
}

const CreateTest: React.FC<CreateTestProps> = ({ userType, onClose, onSubmit }) => {
  const [testData, setTestData] = useState({
    testName: '',
    subject: '',
    class: '',
    duration: '',
    totalMarks: 0,
    instructions: '',
    scheduledDate: '',
    scheduledTime: '',
    isPublished: false,
  });

  const [questions, setQuestions] = useState<Question[]>([
    {
      id: '1',
      question: '',
      type: 'MCQ',
      marks: 1,
      options: ['', '', '', ''],
      correctAnswer: '',
    },
  ]);

  const subjects = ['Mathematics', 'Science', 'English', 'History', 'Geography', 'Physics', 'Chemistry'];
  const classes = ['Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];

  const handleInputChange = (field: string, value: any) => {
    setTestData({ ...testData, [field]: value });
  };

  const handleQuestionChange = (id: string, field: string, value: any) => {
    setQuestions(
      questions.map((q) =>
        q.id === id ? { ...q, [field]: value } : q
      )
    );
  };

  const handleOptionChange = (questionId: string, optionIndex: number, value: string) => {
    setQuestions(
      questions.map((q) =>
        q.id === questionId
          ? {
              ...q,
              options: q.options?.map((opt, idx) =>
                idx === optionIndex ? value : opt
              ),
            }
          : q
      )
    );
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      id: Date.now().toString(),
      question: '',
      type: 'MCQ',
      marks: 1,
      options: ['', '', '', ''],
      correctAnswer: '',
    };
    setQuestions([...questions, newQuestion]);
  };

  const deleteQuestion = (id: string) => {
    if (questions.length > 1) {
      setQuestions(questions.filter((q) => q.id !== id));
    }
  };

  const duplicateQuestion = (id: string) => {
    const questionToDuplicate = questions.find((q) => q.id === id);
    if (questionToDuplicate) {
      const newQuestion = {
        ...questionToDuplicate,
        id: Date.now().toString(),
      };
      setQuestions([...questions, newQuestion]);
    }
  };

  const calculateTotalMarks = () => {
    return questions.reduce((sum, q) => sum + q.marks, 0);
  };

  const handleSubmit = () => {
    const finalTestData = {
      ...testData,
      totalMarks: calculateTotalMarks(),
      questions,
    };
    console.log('Test Data:', finalTestData);
    if (onSubmit) {
      onSubmit(finalTestData);
    }
  };

  return (
      <Dialog open={true} onClose={onClose} fullScreen>
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="#2c3e50">
            Create New Test
          </Typography>
          <Typography color="text.secondary" fontSize={14} mt={0.5}>
            {userType === 'teacher' 
              ? 'Design and schedule assessments for your students'
              : 'Create practice tests for your child'}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          {onClose && (
            <Button
              variant="outlined"
              startIcon={<Close />}
              onClick={onClose}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#e0e0e0',
                color: '#666',
              }}
            >
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#5e72e4',
              '&:hover': { bgcolor: '#4c5fc7' },
            }}
          >
            Save Test
          </Button>
        </Stack>
      </Box>

      {/* Test Information Card */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          border: '1px solid #f0f0f0',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Assignment sx={{ color: '#5e72e4', fontSize: 24 }} />
            <Typography variant="h6" fontWeight={700} color="#2c3e50">
              Test Information
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Test Name"
                placeholder="e.g., Mid-term Mathematics Test"
                value={testData.testName}
                onChange={(e) => handleInputChange('testName', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Assignment sx={{ fontSize: 20, color: '#999' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={testData.subject}
                  label="Subject"
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <Subject sx={{ fontSize: 20, color: '#999' }} />
                    </InputAdornment>
                  }
                >
                  {subjects.map((subject) => (
                    <MenuItem key={subject} value={subject}>
                      {subject}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Class</InputLabel>
                <Select
                  value={testData.class}
                  label="Class"
                  onChange={(e) => handleInputChange('class', e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <Class sx={{ fontSize: 20, color: '#999' }} />
                    </InputAdornment>
                  }
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls} value={cls}>
                      {cls}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                value={testData.duration}
                onChange={(e) => handleInputChange('duration', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTime sx={{ fontSize: 20, color: '#999' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Scheduled Date"
                type="date"
                value={testData.scheduledDate}
                onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Schedule sx={{ fontSize: 20, color: '#999' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Scheduled Time"
                type="time"
                value={testData.scheduledTime}
                onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Instructions"
                multiline
                rows={3}
                placeholder="Enter test instructions for students..."
                value={testData.instructions}
                onChange={(e) => handleInputChange('instructions', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={testData.isPublished}
                      onChange={(e) => handleInputChange('isPublished', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={500}>Publish Immediately</Typography>
                      <Tooltip title="When enabled, students can see and attempt this test right away">
                        <Info sx={{ fontSize: 18, color: '#999' }} />
                      </Tooltip>
                    </Box>
                  }
                />

                <Chip
                  icon={<CheckCircle />}
                  label={`Total Marks: ${calculateTotalMarks()}`}
                  sx={{
                    bgcolor: '#e6f7ed',
                    color: '#2e7d32',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Questions Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={700} color="#2c3e50">
            Questions ({questions.length})
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={addQuestion}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#5e72e4',
              color: '#5e72e4',
              '&:hover': { bgcolor: '#f0f3ff' },
            }}
          >
            Add Question
          </Button>
        </Box>

        {questions.map((question, index) => (
          <Card
            key={question.id}
            sx={{
              mb: 2,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              border: '1px solid #f0f0f0',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              {/* Question Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Chip
                  label={`Question ${index + 1}`}
                  sx={{
                    bgcolor: '#f0f7ff',
                    color: '#1976d2',
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                />

                <Stack direction="row" spacing={1}>
                  <Tooltip title="Duplicate Question">
                    <IconButton
                      size="small"
                      onClick={() => duplicateQuestion(question.id)}
                      sx={{ color: '#666' }}
                    >
                      <ContentCopy sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Question">
                    <IconButton
                      size="small"
                      onClick={() => deleteQuestion(question.id)}
                      disabled={questions.length === 1}
                      sx={{ color: '#d32f2f' }}
                    >
                      <Delete sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Question Text"
                    multiline
                    rows={2}
                    placeholder="Enter your question here..."
                    value={question.question}
                    onChange={(e) =>
                      handleQuestionChange(question.id, 'question', e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Question Type</InputLabel>
                    <Select
                      value={question.type}
                      label="Question Type"
                      onChange={(e) =>
                        handleQuestionChange(question.id, 'type', e.target.value)
                      }
                    >
                      <MenuItem value="MCQ">Multiple Choice (MCQ)</MenuItem>
                      <MenuItem value="Short">Short Answer</MenuItem>
                      <MenuItem value="Long">Long Answer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Marks"
                    type="number"
                    value={question.marks}
                    onChange={(e) =>
                      handleQuestionChange(question.id, 'marks', parseInt(e.target.value) || 0)
                    }
                    inputProps={{ min: 1 }}
                  />
                </Grid>

                {/* MCQ Options */}
                {question.type === 'MCQ' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        color="#666"
                        mb={2}
                      >
                        Answer Options
                      </Typography>
                    </Grid>

                    {question.options?.map((option, optIdx) => (
                      <Grid item xs={12} sm={6} key={optIdx}>
                        <TextField
                          fullWidth
                          label={`Option ${optIdx + 1}`}
                          value={option}
                          onChange={(e) =>
                            handleOptionChange(question.id, optIdx, e.target.value)
                          }
                          placeholder={`Enter option ${optIdx + 1}`}
                        />
                      </Grid>
                    ))}

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Correct Answer</InputLabel>
                        <Select
                          value={question.correctAnswer}
                          label="Correct Answer"
                          onChange={(e) =>
                            handleQuestionChange(question.id, 'correctAnswer', e.target.value)
                          }
                        >
                          {question.options?.map((opt, idx) => (
                            <MenuItem key={idx} value={opt} disabled={!opt}>
                              {opt || `Option ${idx + 1} (empty)`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                {/* Short/Long Answer Placeholder */}
                {(question.type === 'Short' || question.type === 'Long') && (
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: '#f8f9fa',
                        border: '1px dashed #ddd',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        Students will type their answer here during the test
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Summary Footer */}
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          border: '1px solid #f0f0f0',
          position: 'sticky',
          bottom: 20,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Questions
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {questions.length}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Marks
                </Typography>
                <Typography variant="h6" fontWeight={700} color="#2e7d32">
                  {calculateTotalMarks()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {testData.duration || '—'} min
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              size="large"
              startIcon={<Save />}
              onClick={handleSubmit}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#5e72e4',
                px: 4,
                '&:hover': { bgcolor: '#4c5fc7' },
              }}
            >
              Save & Publish Test
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
      </Dialog>
  );
};

export default CreateTest;